import { DatePicker, Input } from 'antd';
import moment from 'moment-timezone';
import React from 'react';
import { Section } from '../../../components/form-fields/section';
import { Doc31EditionSettings } from '../../../types/doc31';
import { describeDoc31Path, doc31FieldId } from '../../../utils/doc31';
import { fromPickerValue, SetupField, SetupFormApi } from './fields';
import style from './style.module.scss';

const DATE_TIME_FORMAT = 'YYYY-MM-DD HH:mm';

type DateField = Exclude<keyof Doc31EditionSettings, 'legalVersion'>;

const DATE_FIELDS: { field: DateField; nullable: boolean }[] = [
    { field: 'playStartsAt', nullable: false },
    { field: 'playEndsAt', nullable: false },
    { field: 'earningStartsAt', nullable: false },
    { field: 'earningEndsAt', nullable: false },
    { field: 'redemptionStartsAt', nullable: true },
    { field: 'redemptionEndsAt', nullable: true },
];

interface Props {
    form: SetupFormApi;
    /** The unsaved play window, for the page header's preview. */
    onPlayWindowChange: (window: {
        playStartsAt: string;
        playEndsAt: string;
    }) => void;
    /** The section's own save, on its title row. */
    saveAction: React.ReactNode;
}

export const EditionSection = ({
    form,
    onPlayWindowChange,
    saveAction,
}: Props) => {
    const { edition } = form.values;

    const onDateChange = (field: DateField, value: moment.Moment | null) => {
        const iso = fromPickerValue(value, form.timezone);
        form.setValue(`edition.${field}`, iso);
        if ((field === 'playStartsAt' || field === 'playEndsAt') && iso) {
            onPlayWindowChange({
                playStartsAt: edition.playStartsAt,
                playEndsAt: edition.playEndsAt,
                [field]: iso,
            });
        }
    };

    return (
        <Section
            id={doc31FieldId('edition')}
            title="Dates and rules version"
            headerActions={
                <>
                    <span className={style.section_note}>
                        Times in {form.timezone}
                    </span>
                    {saveAction}
                </>
            }
        >
            <div className={style.grid_pairs}>
                {DATE_FIELDS.map(({ field, nullable }) => {
                    const path = `edition.${field}`;
                    const iso = edition[field];
                    return (
                        <SetupField
                            key={field}
                            path={path}
                            label={describeDoc31Path(path)}
                        >
                            <DatePicker
                                value={
                                    iso ? moment.tz(iso, form.timezone) : null
                                }
                                format={DATE_TIME_FORMAT}
                                showTime={{ format: 'HH:mm', minuteStep: 5 }}
                                allowClear={nullable}
                                showNow={false}
                                placeholder={nullable ? 'No limit' : undefined}
                                disabled={form.readOnly}
                                onChange={(value) => onDateChange(field, value)}
                            />
                        </SetupField>
                    );
                })}
                <SetupField
                    path="edition.legalVersion"
                    label={describeDoc31Path('edition.legalVersion')}
                >
                    <Input
                        value={edition.legalVersion}
                        disabled={form.readOnly}
                        onChange={(event) =>
                            form.setValue(
                                'edition.legalVersion',
                                event.target.value
                            )
                        }
                    />
                </SetupField>
            </div>
        </Section>
    );
};
